import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { api, apiError, fmtHours, fmtDate, timeInput, MONTHS } from "@/lib/api";

const now = new Date();
const today = now.toISOString().slice(0, 10);

export default function AdminRecords() {
  const [month, setMonth] = useState(now.getMonth() + 1);
  const year = now.getFullYear();
  const [operators, setOperators] = useState([]);
  const [records, setRecords] = useState([]);
  const [filterOp, setFilterOp] = useState("");
  const [form, setForm] = useState({ operator_id: "", day: today, clock_in: "09:00", clock_out: "13:00", note: "" });
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({ day: "", clock_in: "", clock_out: "" });

  const load = useCallback(async () => {
    try {
      const [o, r] = await Promise.all([
        api.get("/operators"),
        api.get("/attendance/records", { params: { year, month, ...(filterOp ? { operator_id: filterOp } : {}) } }),
      ]);
      setOperators(o.data.filter((x) => x.role === "operator"));
      setRecords(r.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [year, month, filterOp]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/attendance", form);
      toast.success("Timbratura aggiunta");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const save = async (id) => {
    try {
      await api.put(`/attendance/${id}`, editValues);
      toast.success("Timbratura aggiornata");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/attendance/${id}`);
      toast.success("Timbratura eliminata");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-records">
      <div>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold">Timbrature</h1>
        <p className="text-sm text-gray-600 mt-1">Aggiungi, correggi o elimina le timbrature del team.</p>
      </div>

      <form onSubmit={add} className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-600">Operatore</label>
          <select
            data-testid="record-operator"
            required
            value={form.operator_id}
            onChange={(e) => setForm({ ...form, operator_id: e.target.value })}
            className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm"
          >
            <option value="">Seleziona</option>
            {operators.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-600">Giorno</label>
          <input data-testid="record-day" type="date" value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })}
            className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-600">Ingresso</label>
          <input data-testid="record-in" type="time" value={form.clock_in} onChange={(e) => setForm({ ...form, clock_in: e.target.value })}
            className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm font-mono-num" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-600">Uscita</label>
          <input data-testid="record-out" type="time" value={form.clock_out} onChange={(e) => setForm({ ...form, clock_out: e.target.value })}
            className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm font-mono-num" />
        </div>
        <button
          data-testid="record-add-btn"
          className="h-11 px-5 rounded-lg bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors hover:bg-emerald-700 active:scale-95"
        >
          <Plus className="h-4 w-4" /> Aggiungi
        </button>
      </form>

      <div className="flex gap-3">
        <select
          data-testid="records-month-select"
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
          className="h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm"
        >
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m} {year}</option>)}
        </select>
        <select
          data-testid="records-operator-filter"
          value={filterOp}
          onChange={(e) => setFilterOp(e.target.value)}
          className="h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm"
        >
          <option value="">Tutti gli operatori</option>
          {operators.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        {records.length === 0 ? (
          <p data-testid="records-empty" className="text-sm text-gray-600">Nessuna timbratura nel periodo.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-gray-600">
                <th className="pb-3">Operatore</th><th className="pb-3">Giorno</th><th className="pb-3">Ingresso</th>
                <th className="pb-3">Uscita</th><th className="pb-3">Ore</th><th className="pb-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id} data-testid={`admin-record-row-${r.id}`} className="border-t border-gray-100">
                  <td className="py-3 font-medium">{r.operator_name}</td>
                  {editing === r.id ? (
                    <>
                      <td className="py-3">
                        <input data-testid={`edit-day-${r.id}`} type="date" value={editValues.day}
                          onChange={(e) => setEditValues({ ...editValues, day: e.target.value })}
                          className="h-10 rounded-lg border border-gray-200 px-2 text-sm" />
                      </td>
                      <td className="py-3">
                        <input data-testid={`edit-in-${r.id}`} type="time" value={editValues.clock_in}
                          onChange={(e) => setEditValues({ ...editValues, clock_in: e.target.value })}
                          className="h-10 rounded-lg border border-gray-200 px-2 text-sm font-mono-num" />
                      </td>
                      <td className="py-3">
                        <input data-testid={`edit-out-${r.id}`} type="time" value={editValues.clock_out}
                          onChange={(e) => setEditValues({ ...editValues, clock_out: e.target.value })}
                          className="h-10 rounded-lg border border-gray-200 px-2 text-sm font-mono-num" />
                      </td>
                      <td className="py-3">—</td>
                    </>
                  ) : (
                    <>
                      <td className="py-3">{fmtDate(r.day)}</td>
                      <td className="py-3 font-mono-num">{timeInput(r.clock_in)}</td>
                      <td className="py-3 font-mono-num">{r.clock_out ? timeInput(r.clock_out) : "in corso"}</td>
                      <td className="py-3 font-mono-num">{r.minutes != null ? fmtHours(r.minutes) : "—"}</td>
                    </>
                  )}
                  <td className="py-3">
                    <div className="flex justify-end gap-2">
                      {editing === r.id ? (
                        <>
                          <button
                            data-testid={`record-save-${r.id}`}
                            onClick={() => save(r.id)}
                            className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1"
                          >
                            <Save className="h-3.5 w-3.5" /> Salva
                          </button>
                          <button
                            data-testid={`record-cancel-${r.id}`}
                            onClick={() => setEditing(null)}
                            className="h-9 px-3 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600"
                          >
                            Annulla
                          </button>
                        </>
                      ) : (
                        <button
                          data-testid={`record-edit-${r.id}`}
                          onClick={() => {
                            setEditing(r.id);
                            setEditValues({ day: r.day, clock_in: timeInput(r.clock_in), clock_out: timeInput(r.clock_out) });
                          }}
                          className="h-9 px-3 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                        >
                          Modifica
                        </button>
                      )}
                      <button
                        data-testid={`record-delete-${r.id}`}
                        onClick={() => remove(r.id)}
                        aria-label="Elimina timbratura"
                        className="h-9 px-3 rounded-lg border border-red-200 text-red-600 text-xs font-semibold flex items-center transition-colors hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
